/**
 * المرحلة 1.35 — the centre's consents as printable A4 sheets (photos 5–9),
 * filled from the file and the stay (domain/centerForms.js), the signatures,
 * the thumbprints and the receptionist's line left for the pen. The price
 * acknowledgement prints the centre's live price list above its «إقرار».
 *
 * Shown under the `stayBooklet` policy: the child file's «الإقرارات» sheet,
 * and the discharge's own consent once a stay ends.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Field, Icon, Input, Spinner } from '../../components/common';
import { staysApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { useI18n } from '../../i18n/I18nProvider';
import { formatDate, formatMoney, formatTime } from '../../domain/locale';
import { CONSENTS, CONSENT_SIGNS, THUMBS, consentParts, consentValues, priceTable } from '../../domain/centerForms';
import { Line, PrintButton, SheetHead, weekday } from './Paper';

const ICONS = {
  admission: 'clipboard-check',
  on_request: 'log-out',
  recovered: 'heart-pulse',
  death: 'file-text',
  against_advice: 'alert-triangle',
  central_line: 'syringe',
  payment: 'wallet',
  prices: 'tag',
};

/** The wording with its values bold on their lines, or the empty line. */
function Filled({ template, values }) {
  return (
    <p className="cp-text">
      {consentParts(template, values).map((p, i) =>
        p.field ? (
          p.value ? (
            <b key={i} className="cp-fill">
              {p.value}
            </b>
          ) : (
            <span key={i} className="cp-blank" aria-hidden="true" />
          )
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </p>
  );
}

/** The live price list, by category, as the acknowledgement's first half. */
function PriceSheet() {
  const { t } = useI18n();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    staysApi
      .options()
      .then((o) => setRows(priceTable(o.units || [])))
      .catch(() => setRows([]));
  }, []);
  if (!rows) return <Spinner label={t('centerConsent.prices.loading')} variant="inline" />;
  return (
    <div className="cp-prices" data-testid="consent-price-list">
      {rows.map((group) => (
        <table key={group.category} className="cf-table cp-prices__table">
          <thead>
            <tr>
              <th>{group.category}</th>
              <th>{t('centerConsent.prices.price')}</th>
            </tr>
          </thead>
          <tbody>
            {group.items.map((item, i) => (
              <tr key={i}>
                <td>
                  {item.label}
                  {item.unit && item.unit !== 'once' && <span className="cp-unit"> / {t(`priceList.units.${item.unit}`)}</span>}
                </td>
                <td className="cp-money">{formatMoney(item.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
      <p className="cp-note">{t('centerConsent.prices.note')}</p>
    </div>
  );
}

/**
 * @param {{ patient: object, profile?: object, stay?: object|null, defaultForm?: string }} props
 */
export function ConsentPrints({ patient, profile = {}, stay = null, defaultForm = 'admission' }) {
  const { t, lang } = useI18n();
  const { institutionName } = usePermissions();
  const ref = useRef(null);
  const [form, setForm] = useState(CONSENTS.includes(defaultForm) ? defaultForm : 'admission');
  const [procedure, setProcedure] = useState(() => t('centerConsent.central_line.procedure'));
  const centre = institutionName || t('childFile.admission.theCentre');
  const values = useMemo(
    () => consentValues(form, { patient, profile, stay, centre, procedure }, { t, fmt: { date: (v) => formatDate(v), time: (v) => formatTime(v), weekday: (v) => weekday(v, lang) } }),
    [form, patient, profile, stay, centre, procedure, t, lang],
  );
  if (!patient) return <Spinner label={t('childFile.loading')} variant="inline" />;
  const declarant = form === 'against_advice' || form === 'central_line';
  return (
    <div className="cp" data-testid="consent-prints">
      <div className="cp-menu" role="radiogroup" aria-label={t('centerConsent.menu')}>
        {CONSENTS.map((id) => (
          <button key={id} type="button" role="radio" aria-checked={form === id} className={`cp-pick${form === id ? ' cp-pick--on' : ''}`} onClick={() => setForm(id)} data-testid={`consent-${id}`}>
            <Icon name={ICONS[id]} size={15} /> {t(`centerConsent.${id}.label`)}
          </button>
        ))}
      </div>
      {form === 'central_line' && (
        <Field label={t('centerConsent.central_line.procedureLabel')}>
          <Input value={procedure} onChange={(e) => setProcedure(e.target.value)} data-testid="consent-procedure" />
        </Field>
      )}
      <div className="flex justify-end">
        <PrintButton target={ref} testId="consent-print" />
      </div>
      <article ref={ref} className="cf-paper cp-sheet" data-testid="consent-sheet">
        <SheetHead title={t(`centerConsent.${form}.title`, { centre })} subtitle={form === 'prices' ? t('centerConsent.prices.subtitle') : undefined} />
        {form === 'prices' && <PriceSheet />}
        {form === 'prices' && <h4 className="cp-heading">{t('centerConsent.prices.ackTitle')}</h4>}
        <Filled template={t(`centerConsent.${form}.body`)} values={values} />
        {declarant && <p className="cp-declarant">{t('centerConsent.sign.declarant')}</p>}
        <div className="cf-sign cp-signs">
          {CONSENT_SIGNS[form].map((s) => (
            <Line key={s} label={t(`centerConsent.sign.${s}`)}>
              {s === 'date' ? formatDate(new Date()) : ''}
            </Line>
          ))}
        </div>
        {THUMBS.includes(form) && (
          <div className="cp-thumbs">
            <p className="cp-thumbs__title">{t('centerConsent.thumbs.title')}</p>
            <div className="cp-thumbs__boxes">
              {['right', 'left'].map((h) => (
                <span key={h} className="cp-thumb">
                  {t(`centerConsent.thumbs.${h}`)}
                </span>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}

export default ConsentPrints;
